import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Plus } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { isExternalSdRole } from "@sd/lib/sdRoles";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TicketStatusSelectItems } from "@sd/components/common/TicketStatusSelectItems";
import { TablePager } from "@sd/components/common/TablePager";
import { TicketFilterBar } from "@sd/components/tickets/TicketFilterBar";
import { TicketList } from "@sd/components/tickets/TicketList";
import { TicketDetail } from "@sd/components/tickets/TicketDetail";
import { AddTicketCommentDialog } from "@sd/components/tickets/AddTicketCommentDialog";
import { UserPanel } from "@sd/components/tickets/UserPanel";
import { PortalTicketCreateDialog } from "@sd/components/tickets/PortalTicketCreateDialog";
import { DeleteTicketConfirmDialog } from "@sd/components/common/DeleteTicketConfirmDialog";
import { formatTicketDeleteLabel } from "@sd/lib/ticketDeleteLabel";
import { PortalTicketEditDialog } from "@sd/components/tickets/PortalTicketEditDialog";
import {
  ASSIGNED_SELECT_NONE,
  normalizeAgentEmployeeList,
} from "@sd/lib/adminTicketUtils";
import { buildTrackerReportReturnPath } from "@sd/lib/trackerReportDrillDown";
import { buildStatusDashboardReturnPath } from "@sd/lib/statusDashboardDrillDown";
import {
  appendWorkbenchSelectedTicketToPath,
  buildTicketDetailPath,
  parseWorkbenchSelectedTicketFromSearchParams,
} from "@sd/lib/ticketDetailPath";
import { TicketListDrillDownBanner } from "@sd/components/tickets/TicketListDrillDownBanner";
import { useAgentCompactLayout } from "@sd/hooks/use-mobile";
import { useAgentWorkbenchTicketDetail } from "@sd/hooks/useAgentWorkbenchTicketDetail";
import { useTicketWorkbenchComments } from "@sd/hooks/useTicketWorkbenchComments";
import { putTicketPriorityId } from "@sd/lib/putTicketPriorityId";
import { putTicketFormilyStatus } from "@sd/lib/putTicketFormilyStatus";
import { putTicketIsVip } from "@sd/lib/putTicketIsVip";
import { putTicketIsSla } from "@sd/lib/putTicketIsSla";
import { bulkUpdateTickets } from "@sd/lib/bulkUpdateTickets";
import { formatStatusLabel, normalizeStatus, AGENT_STATUS_CHANGE_OPTIONS } from "@sd/constants/ticketStatuses";
import { normalizePriority } from "@sd/lib/ticketStreamUtils";
import ServiceDeskAPIService, {
  serviceDeskProjectAssignedQueryKey,
  serviceDeskTicketCreateListQueryKey,
} from "@sd/services/APIs";
import type { Ticket, TicketStatus } from "@sd/types/ticket";
import type { useTicketsPage } from "@sd/hooks/useTicketsPage";
import { usePtsModuleFilterOptions } from "@sd/hooks/usePtsModuleFilterOptions";
import { DEFAULT_MODULE_FILTER } from "@sd/lib/ticketFilterDefaults";

type TicketsVm = ReturnType<typeof useTicketsPage>;

export function AgentTicketWorkbench({
  vm,
  isAgentOnly,
}: {
  vm: TicketsVm;
  isAgentOnly: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { queue: queueParam } = useParams<{ queue?: string }>();
  const { role } = useAuth();
  const isSuperAgent = role === "ServiceDesk_SuperAgent";
  const isExternalUser = isExternalSdRole(role);
  /** Super Agent All Tickets only — project-wise assignee list via is_pts. */
  const isSuperAgentAllTickets = isSuperAgent && queueParam === "all";
  /** Super Agent only — All Tickets + My Tickets (not regular Agent panel). */
  const showSuperAgentSlaToggle =
    isSuperAgent &&
    (queueParam === "all" || queueParam === "my-tickets");
  /** VIP — Super Agent on all workbench queues (unchanged from before SLA work). */
  const showVipToggle = !isAgentOnly;
  /** Bulk status/assign — Agent + Super Agent workbench (same API as Admin). */
  const showBulkActions =
    ((isAgentOnly && !isExternalUser) || isSuperAgent);
  const ticketListReturnTo = `${location.pathname}${location.search}`;
  const isCompact = useAgentCompactLayout();
  const showDesktopSplit = !isCompact;
  const ticketFromUrl = parseWorkbenchSelectedTicketFromSearchParams(
    new URLSearchParams(location.search)
  );
  const [mobilePane, setMobilePane] = useState<"list" | "detail">(() =>
    ticketFromUrl ? "detail" : "list"
  );
  const [editTicketOpen, setEditTicketOpen] = useState(false);
  const [editTicketId, setEditTicketId] = useState<string | null>(null);
  const [deleteTicketTarget, setDeleteTicketTarget] = useState<Ticket | null>(null);
  const [deleteTicketConfirming, setDeleteTicketConfirming] = useState(false);
  const [addCommentOpen, setAddCommentOpen] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);

  const {
    search,
    setSearch,
    statusFilters,
    setStatusFilters,
    priorityFilters,
    setPriorityFilters,
    categoryFilters,
    setCategoryFilters,
    typeFilter,
    setTypeFilter,
    projectFilters,
    setProjectFilters,
    projectFilterOptions,
    assigneeFilters,
    setAssigneeFilters,
    requesterFilters,
    setRequesterFilters,
    createdDateFilter,
    setCreatedDateFilter,
    vipFilter,
    setVipFilter,
    slaFilter,
    setSlaFilter,
    createUpdateFilter,
    setCreateUpdateFilter,
    moduleFilter,
    setModuleFilter,
    requesterFilterOptions,
    categories,
    totalCount,
    totalPages,
    pageNum,
    setPageNum,
    pageSize,
    setPageSize,
    isPending,
    isFetching,
    isFilterDebouncing,
    agentTickets,
    selectedAgentTicketId,
    setSelectedAgentTicketId,
    selectAgentTicket,
    removeAgentTicket,
    assigneeSelectOptions,
    priorityOptions,
    patchAgentTicket,
    handleAssign,
    queryClient,
    selectedIds,
    setSelectedIds,
    toggleSelect,
    showQuickAddModal,
    setShowQuickAddModal,
    onTicketSchemaSaved,
    clearAllFilters,
    fromTrackerReport,
    reportReturnProjectId,
    trackerReportReturnFilters,
    fromStatusDashboard,
    statusDashboardReturnFilters,
    mergeAgentDisplayTicket,
  } = vm;

  const {
    showModuleFilter,
    moduleOptions,
    modulesLoading,
  } = usePtsModuleFilterOptions(
    projectFilters,
    !isAgentOnly ? moduleFilter : DEFAULT_MODULE_FILTER
  );

  const activeDetailTicketId = selectedAgentTicketId;

  const {
    ticket: detailTicket,
    isLoading: detailLoading,
    isNotFound: detailNotFound,
  } = useAgentWorkbenchTicketDetail(activeDetailTicketId);

  const {
    notes: workbenchNotes,
    notesPending: workbenchNotesLoading,
    notesError: workbenchNotesError,
    resolveCommentAuthor,
  } = useTicketWorkbenchComments(activeDetailTicketId);

  const displayTicket = useMemo(
    () => (detailNotFound ? null : mergeAgentDisplayTicket(detailTicket)),
    [mergeAgentDisplayTicket, detailTicket, detailNotFound]
  );

  useEffect(() => {
    if (!detailNotFound || !activeDetailTicketId) return;
    removeAgentTicket(activeDetailTicketId);
  }, [detailNotFound, activeDetailTicketId, removeAgentTicket]);

  // Assignee dropdown option source (is_pts):
  //   is_pts:false → agent_employee_list (all agents)
  //   is_pts:true  → project-assigned/?project_id={ticket.project}
  // Agent (all queues): needs is_exit:true else lock.
  // Super Agent: ONLY on All Tickets tab — option source follows is_pts per ticket/project.
  // Super Agent My Tickets: always agent_employee_list (no project-assigned switch).
  const ticketExitAllowed = displayTicket?.isExit === true;
  const canPickAssigneeSource = isAgentOnly
    ? ticketExitAllowed
    : isSuperAgentAllTickets;

  const shouldUseProjectAssignedAgents =
    canPickAssigneeSource &&
    displayTicket?.isPts === true &&
    Boolean(displayTicket.projectId);

  // Super Agent All Tickets: when not on project-assigned, use all employees.
  // Agent: only when is_exit + is_pts:false.
  const shouldUseAllAgents =
    canPickAssigneeSource &&
    (displayTicket?.isPts === false ||
      (isSuperAgentAllTickets && !shouldUseProjectAssignedAgents));

  const { data: projectAssignedPayload, isFetching: projectAssignedLoading } =
    useQuery({
      queryKey: serviceDeskProjectAssignedQueryKey(displayTicket?.projectId),
      queryFn: async () => {
        const response = await ServiceDeskAPIService.projectAssigned.list(
          displayTicket!.projectId!
        );
        return response.data as unknown;
      },
      enabled: shouldUseProjectAssignedAgents,
      staleTime: 60_000,
      retry: 1,
    });

  const projectAssignedOptions = useMemo(
    () => normalizeAgentEmployeeList(projectAssignedPayload),
    [projectAssignedPayload]
  );

  // Super Agent All Tickets: if project-assigned empty after load, fall back to all employees.
  const detailAssigneeOptions = shouldUseProjectAssignedAgents
    ? projectAssignedOptions.length > 0 || projectAssignedLoading
      ? projectAssignedOptions
      : assigneeSelectOptions
    : assigneeSelectOptions;

  /** Agent only — Super Agent dropdown is never locked (TicketDetail: !isAgentOnly). */
  const agentAssigneeEditable =
    isAgentOnly &&
    !isExternalUser &&
    (shouldUseAllAgents || shouldUseProjectAssignedAgents);

  const detailPaneActive = Boolean(activeDetailTicketId);
  const detailStillLoading =
    detailPaneActive && !displayTicket && (detailLoading || !detailTicket);

  const isUpdatingTickets = isFetching || isFilterDebouncing;

  const handleSelectTicket = (ticketId: string) => {
    selectAgentTicket(ticketId);
    if (isCompact) {
      setMobilePane("detail");
    }
  };

  const openFullTicketDetail = useCallback(
    (ticketId: string) => {
      navigate(
        buildTicketDetailPath(ticketId, {
          returnTo: appendWorkbenchSelectedTicketToPath(
            ticketListReturnTo,
            ticketId
          ),
        })
      );
    },
    [navigate, ticketListReturnTo]
  );

  useEffect(() => {
    if (!isCompact || !selectedAgentTicketId) return;
    setMobilePane("detail");
  }, [isCompact, selectedAgentTicketId]);

  const handleEditTicket = useCallback((ticketId: string) => {
    setEditTicketId(ticketId);
    setEditTicketOpen(true);
  }, []);

  const handleEditTicketUpdated = () => {
    onTicketSchemaSaved();
    toast.success("Ticket updated successfully");
  };

  const requestDeleteTicket = useCallback((ticket: Ticket) => {
    setDeleteTicketTarget(ticket);
  }, []);

  const confirmDeleteTicket = useCallback(async () => {
    const ticket = deleteTicketTarget;
    if (!ticket) return;
    setDeleteTicketConfirming(true);
    try {
      await ServiceDeskAPIService.tickets.deleteCreatedTicket(ticket.id, {
        skipErrorNotification: true,
      });
      removeAgentTicket(ticket.id);
      setDeleteTicketTarget(null);
      onTicketSchemaSaved();
      toast.success("Ticket deleted");
    } catch {
      toast.error("Failed to delete ticket");
    } finally {
      setDeleteTicketConfirming(false);
    }
  }, [
    deleteTicketTarget,
    onTicketSchemaSaved,
    removeAgentTicket,
  ]);

  const toggleBulkSelectAll = () => {
    const allVisibleSelected =
      agentTickets.length > 0 &&
      agentTickets.every(t => selectedIds.has(t.id));
    if (allVisibleSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        for (const t of agentTickets) next.delete(t.id);
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        for (const t of agentTickets) next.add(t.id);
        return next;
      });
    }
  };

  const handleBulkStatusChange = async (status: string) => {
    const next = normalizeStatus(status) as TicketStatus;
    const ids = [...selectedIds];
    if (!showBulkActions || ids.length === 0 || bulkUpdating) {
      return;
    }
    setBulkUpdating(true);
    try {
      const result = await bulkUpdateTickets(queryClient, {
        ids,
        status: next,
      });
      if (!result.ok) {
        toast.error(result.message);
        return;
      }
      for (const id of ids) {
        patchAgentTicket(id, { status: next });
      }
      toast.success(`${ids.length} ticket(s) → ${formatStatusLabel(next)}`);
      setSelectedIds(new Set());
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleBulkAssign = async (agentEmployeeId: string) => {
    if (
      !showBulkActions ||
      !agentEmployeeId ||
      agentEmployeeId === ASSIGNED_SELECT_NONE
    ) {
      return;
    }
    const ids = [...selectedIds];
    if (ids.length === 0 || bulkUpdating) {
      return;
    }
    const opt = assigneeSelectOptions.find(o => o.id === agentEmployeeId);
    const label = opt?.name ?? agentEmployeeId;
    setBulkUpdating(true);
    try {
      const result = await bulkUpdateTickets(queryClient, {
        ids,
        assigned_to: agentEmployeeId,
      });
      if (!result.ok) {
        toast.error(result.message);
        return;
      }
      for (const id of ids) {
        patchAgentTicket(id, { assignee: agentEmployeeId });
      }
      toast.success(`${ids.length} ticket(s) assigned to ${label}`);
      setSelectedIds(new Set());
    } finally {
      setBulkUpdating(false);
    }
  };

  useEffect(() => {
    setAddCommentOpen(false);
  }, [activeDetailTicketId]);

  const renderTicketDetail = (ticket: Ticket) => {
    // Agent: hide Edit when is_exit:false. Super Agent: Edit on every ticket.
    const canEditTicket = isAgentOnly ? ticket.isExit !== false : true;
    // Agent (any queue) OR Super Agent All Tickets — is_pts-aware assignee lists.
    const usePtsAwareAssigneeOptions =
      isAgentOnly || isSuperAgentAllTickets;
    return (
    <TicketDetail
      ticket={ticket}
      videoPreviewControls={isSuperAgent}
      showWorkbenchComments
      onOpenAddComment={() => setAddCommentOpen(true)}
      workbenchNotes={workbenchNotes}
      workbenchNotesLoading={workbenchNotesLoading}
      workbenchNotesError={workbenchNotesError}
      resolveCommentAuthor={resolveCommentAuthor}
      {...(canEditTicket
        ? { onEdit: () => handleEditTicket(ticket.id) }
        : {})}
      onOpenFullView={() => openFullTicketDetail(ticket.id)}
      {...(isSuperAgent
        ? {
            onDelete: () => requestDeleteTicket(ticket),
          }
        : {})}
      onStatusChange={s => {
        patchAgentTicket(ticket.id, {
          status: s as TicketStatus,
        });
        void putTicketFormilyStatus(
          queryClient,
          ticket.id,
          s as TicketStatus,
          ticket.priorityId ? { priority_id: ticket.priorityId } : undefined
        ).then(ok => {
          if (ok) toast.success(`Status updated to ${s}`);
        });
      }}
      priorityOptions={priorityOptions}
      onPriorityChange={priorityId => {
        const opt = priorityOptions.find(p => p.id === priorityId);
        patchAgentTicket(ticket.id, {
          priorityId,
          priorityLabel: opt?.label,
          priority: opt?.label
            ? (normalizePriority(opt.label) as Ticket["priority"])
            : ticket.priority,
        });
        void putTicketPriorityId(queryClient, ticket.id, priorityId).then(ok => {
          if (ok) toast.success("Priority updated");
          else toast.error("Failed to update priority");
        });
      }}
      assigneeOptions={
        usePtsAwareAssigneeOptions
          ? detailAssigneeOptions
          : assigneeSelectOptions
      }
      {...(isAgentOnly
        ? {
            agentAssigneeEditable,
            assigneeOptionsLoading:
              shouldUseProjectAssignedAgents && projectAssignedLoading,
          }
        : isSuperAgentAllTickets
          ? {
              assigneeOptionsLoading:
                shouldUseProjectAssignedAgents && projectAssignedLoading,
            }
          : {})}
      onAssignTo={(agentId, agentName) =>
        handleAssign(ticket.id, agentId, agentName, {
          updateStatus: false,
          preservePriorityId: ticket.priorityId,
        })
      }
      onExtendAutoClose={() => toast.success("Auto-close extended")}
      {...(showSuperAgentSlaToggle && {
        onSlaChange: checked => {
          patchAgentTicket(ticket.id, { isSla: checked });
          void putTicketIsSla(queryClient, ticket.id, checked).then(ok => {
            if (ok) toast.success(checked ? "SLA enabled" : "SLA disabled");
          });
        },
      })}
      {...(showVipToggle && {
        onVipChange: checked => {
          patchAgentTicket(ticket.id, { isVip: checked });
          void putTicketIsVip(queryClient, ticket.id, checked).then(ok => {
            if (ok) toast.success(checked ? "Marked as VIP" : "VIP removed");
          });
        },
      })}
    />
    );
  };

  const handleAddTicketCreated = (
    ticketId: string | null,
    meta?: { subject?: string; uiId?: string | null; crNumber?: string | null }
  ) => {
    onTicketSchemaSaved();

    const uuidRe =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

    const resolveCreatedId = async (): Promise<string | null> => {
      if (ticketId && uuidRe.test(ticketId.trim())) {
        return ticketId.trim();
      }
      const subject = meta?.subject?.trim();
      if (!subject) return null;
      try {
        const res = await ServiceDeskAPIService.tickets.listCreatedTickets({
          search: subject,
          page: 1,
          page_size: 20,
          is_merge: false,
        });
        const raw = res.data as Record<string, unknown> | unknown[];
        const rows: Record<string, unknown>[] = Array.isArray(raw)
          ? (raw as Record<string, unknown>[])
          : Array.isArray((raw as Record<string, unknown>)?.results)
            ? ((raw as Record<string, unknown>).results as Record<
                string,
                unknown
              >[])
            : Array.isArray((raw as Record<string, unknown>)?.data)
              ? ((raw as Record<string, unknown>).data as Record<
                  string,
                  unknown
                >[])
              : Array.isArray((raw as Record<string, unknown>)?.tickets)
                ? ((raw as Record<string, unknown>).tickets as Record<
                    string,
                    unknown
                  >[])
                : [];
        const needle = subject.toLowerCase();
        const scored = rows
          .map(r => {
            const id = String(r.id ?? r.ticket_id ?? "").trim();
            const sub = String(r.subject ?? r.title ?? "")
              .trim()
              .toLowerCase();
            const created = String(r.created_on ?? r.created_at ?? "");
            return { id, sub, created };
          })
          .filter(r => r.id && uuidRe.test(r.id))
          .filter(r => r.sub === needle || r.sub.includes(needle))
          .sort((a, b) => {
            const exactA = a.sub === needle ? 1 : 0;
            const exactB = b.sub === needle ? 1 : 0;
            if (exactA !== exactB) return exactB - exactA;
            return b.created.localeCompare(a.created);
          });
        return scored[0]?.id ?? null;
      } catch {
        return null;
      }
    };

    void (async () => {
      const resolvedId = await resolveCreatedId();
      await queryClient.refetchQueries({
        queryKey: serviceDeskTicketCreateListQueryKey,
      });
      if (resolvedId) {
        selectAgentTicket(resolvedId);
      }
    })();

    toast.success(
      meta?.subject
        ? `Created: ${meta.subject}`
        : "Ticket created successfully",
      (() => {
        const parts = [
          meta?.uiId ? String(meta.uiId) : "",
          !isAgentOnly && meta?.crNumber ? String(meta.crNumber) : "",
        ].filter(Boolean);
        return parts.length > 0 ? { description: parts.join(" · ") } : undefined;
      })()
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="relative z-30 shrink-0 overflow-visible border-b border-border bg-card px-3 py-3 shadow-sm">
        {fromTrackerReport ? (
          <TicketListDrillDownBanner
            variant="tracker-report"
            backLabel="Back to Tracker Report"
            onBack={() =>
              navigate(
                buildTrackerReportReturnPath(
                  projectFilters.length > 0
                    ? {
                        ...trackerReportReturnFilters,
                        projectFilters,
                      }
                    : trackerReportReturnFilters,
                  "agent"
                )
              )
            }
            projectOptions={projectFilterOptions}
            projectFilters={
              projectFilters.length > 0
                ? projectFilters
                : reportReturnProjectId
                  ? [reportReturnProjectId]
                  : []
            }
            statusFilters={statusFilters}
            typeFilter={typeFilter}
          />
        ) : fromStatusDashboard ? (
          <TicketListDrillDownBanner
            variant="status-dashboard"
            backLabel="Back to Dashboard Project Status"
            onBack={() =>
              navigate(
                buildStatusDashboardReturnPath(
                  "agent",
                  statusDashboardReturnFilters
                )
              )
            }
            projectOptions={projectFilterOptions}
            projectFilters={projectFilters}
            statusFilters={statusFilters}
            createdDateFilter={createdDateFilter}
          />
        ) : null}
        {!(isCompact && mobilePane === "detail") ? (
        <TicketFilterBar
          search={search}
          onSearchChange={setSearch}
          statusFilters={statusFilters}
          onStatusFiltersChange={setStatusFilters}
          priorityFilters={priorityFilters}
          onPriorityFiltersChange={setPriorityFilters}
          priorityOptions={priorityOptions}
          categoryFilters={categoryFilters}
          onCategoryFiltersChange={setCategoryFilters}
          categories={categories}
          typeFilter={typeFilter}
          onTypeFilterChange={setTypeFilter}
          projectFilters={projectFilters}
          onProjectFiltersChange={setProjectFilters}
          projectOptions={projectFilterOptions}
          {...(!isExternalUser && {
            assigneeFilters,
            onAssigneeFiltersChange: setAssigneeFilters,
            assigneeOptions: assigneeSelectOptions,
            unassignedFilterValue: ASSIGNED_SELECT_NONE,
            requesterFilters,
            onRequesterFiltersChange: setRequesterFilters,
            requesterOptions: requesterFilterOptions,
          })}
          createdDateFilter={createdDateFilter}
          onCreatedDateFilterChange={setCreatedDateFilter}
          {...(!isAgentOnly && {
            vipFilter,
            onVipFilterChange: setVipFilter,
            slaFilter,
            onSlaFilterChange: setSlaFilter,
            moduleFilter,
            onModuleFilterChange: setModuleFilter,
            moduleOptions,
            showModuleFilter,
            moduleOptionsLoading: modulesLoading,
          })}
          {...(isSuperAgent && {
            createUpdateFilter,
            onCreateUpdateFilterChange: setCreateUpdateFilter,
          })}
          mergeVipSlaFilter={isSuperAgent}
          filterLayout="split"
          onReset={clearAllFilters}
          trailing={
            isUpdatingTickets ? (
              <Badge
                variant="outline"
                className="shrink-0 whitespace-nowrap py-1.5 text-xs text-muted-foreground max-xl:w-full max-xl:justify-center"
              >
                Updating…
              </Badge>
            ) : totalCount !== undefined ? (
              <Badge
                variant="outline"
                className="shrink-0 whitespace-nowrap py-1.5 text-xs max-xl:w-full max-xl:justify-center"
              >
                Page{" "}
                <span className="font-semibold text-foreground">{pageNum}</span>{" "}
                of{" "}
                <span className="font-semibold text-foreground">{totalPages}</span>
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="shrink-0 whitespace-nowrap py-1.5 text-xs text-muted-foreground max-xl:w-full max-xl:justify-center"
              >
                …
              </Badge>
            )
          }
          secondaryTrailing={
            <Button
              className="h-9 shrink-0 gap-2"
              onClick={() => setShowQuickAddModal(true)}
            >
              <Plus className="h-4 w-4" />
              Add Ticket
            </Button>
          }
        />
        ) : null}
        {showBulkActions &&
        selectedIds.size > 0 &&
        !(isCompact && mobilePane === "detail") ? (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 p-3">
            <span className="text-sm font-medium">
              {selectedIds.size} selected
              {bulkUpdating ? " — updating…" : ""}
            </span>
            <Select
              disabled={bulkUpdating}
              onValueChange={handleBulkStatusChange}
            >
              <SelectTrigger className="h-8 w-[130px] text-xs">
                <SelectValue placeholder="Change Status" />
              </SelectTrigger>
              <SelectContent>
                <TicketStatusSelectItems
                  options={
                    isAgentOnly ? AGENT_STATUS_CHANGE_OPTIONS : undefined
                  }
                />
              </SelectContent>
            </Select>
            {isExternalUser ? null : (
            <Select disabled={bulkUpdating} onValueChange={handleBulkAssign}>
              <SelectTrigger className="h-8 w-[140px] text-xs">
                <SelectValue
                  placeholder={
                    assigneeSelectOptions.length
                      ? "Assign To"
                      : "Loading agents…"
                  }
                />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                {assigneeSelectOptions.map(a => (
                  <SelectItem key={`bulk-${a.id}`} value={a.id}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              disabled={bulkUpdating}
              onClick={() => setSelectedIds(new Set())}
            >
              Clear
            </Button>
          </div>
        ) : null}
      </div>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Ticket list — full width on mobile list view; fixed column on desktop */}
        <div
          className={cn(
            "flex h-full shrink-0 flex-col border-r border-border",
            showDesktopSplit && "w-[min(100%,400px)] min-w-[280px] max-w-[32vw] shrink-0 flex",
            !showDesktopSplit &&
              (mobilePane === "detail" ? "hidden" : "flex w-full min-w-0 flex-1")
          )}
        >
          <div className="relative min-h-0 flex-1 overflow-hidden">
            {isUpdatingTickets ? (
              <div
                className="pointer-events-none absolute inset-0 z-10 flex items-start justify-center bg-background/45 pt-8"
                aria-live="polite"
                aria-busy="true"
              >
                <span className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground shadow-sm">
                  Updating tickets…
                </span>
              </div>
            ) : null}
            <TicketList
              tickets={agentTickets}
              selectedTicketId={selectedAgentTicketId}
              onSelectTicket={handleSelectTicket}
              onViewTicket={openFullTicketDetail}
              hideStreamSearch
              paginated
              totalCount={totalCount}
              {...(showBulkActions
                ? {
                    selectionMode: true,
                    selectedTickets: selectedIds,
                    onToggleSelection: toggleSelect,
                    onToggleSelectAll: toggleBulkSelectAll,
                  }
                : {})}
            />
          </div>
          {!isPending && totalCount !== undefined && totalCount > 0 && (
            <TablePager
              compact
              pageNum={pageNum}
              totalPages={totalPages}
              pageSize={pageSize}
              isLoading={isUpdatingTickets}
              onPageChange={setPageNum}
              onPageSizeChange={size => {
                setPageSize(size);
                setPageNum(1);
              }}
            />
          )}
        </div>

        {/* Ticket detail + user panel */}
        <div
          className={cn(
            "min-h-0 min-w-0 flex-1",
            !showDesktopSplit && mobilePane === "list"
              ? "hidden"
              : "flex flex-col overflow-hidden"
          )}
        >
          {detailPaneActive ? (
            <>
              {isCompact && mobilePane === "detail" ? (
                <div className="flex shrink-0 items-center gap-2 border-b border-border bg-card px-3 py-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 gap-1.5 px-2"
                    onClick={() => setMobilePane("list")}
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Tickets
                  </Button>
                  <span className="truncate text-xs font-semibold text-muted-foreground">
                    {displayTicket?.uiId ?? displayTicket?.id ?? "…"}
                  </span>
                </div>
              ) : null}
              {detailStillLoading || !displayTicket ? (
                <div className="flex flex-1 items-center justify-center bg-card text-sm text-muted-foreground">
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Loading ticket…
                </div>
              ) : (
                <div
                  className={cn(
                    "flex min-h-0 min-w-0 flex-1",
                    showDesktopSplit
                      ? "flex-row overflow-hidden"
                      : "flex-col overflow-y-auto"
                  )}
                >
                  <div
                    className={cn(
                      "flex min-h-0 min-w-0 flex-col overflow-hidden",
                      showDesktopSplit ? "flex-1" : "shrink-0"
                    )}
                  >
                    {renderTicketDetail(displayTicket)}
                  </div>
                  <UserPanel ticket={displayTicket} showVip={showVipToggle} stacked={!showDesktopSplit} />
                </div>
              )}
            </>
          ) : (
            <div
              className={cn(
                "flex-1 items-center justify-center bg-card text-sm text-muted-foreground",
                showDesktopSplit ? "flex" : "hidden"
              )}
            >
              Select a ticket from the stream
            </div>
          )}
        </div>
      </div>

      <PortalTicketCreateDialog
        open={showQuickAddModal}
        onOpenChange={setShowQuickAddModal}
        modal={false}
        onCreated={handleAddTicketCreated}
      />
      <PortalTicketEditDialog
        open={editTicketOpen}
        onOpenChange={open => {
          setEditTicketOpen(open);
          if (!open) setEditTicketId(null);
        }}
        ticketId={editTicketId}
        modal={false}
        onUpdated={handleEditTicketUpdated}
      />
      {isSuperAgent ? (
        <DeleteTicketConfirmDialog
          open={deleteTicketTarget !== null}
          onOpenChange={open => {
            if (!open && !deleteTicketConfirming) setDeleteTicketTarget(null);
          }}
          ticketLabel={
            deleteTicketTarget
              ? formatTicketDeleteLabel({
                  title: deleteTicketTarget.title,
                  uiId: deleteTicketTarget.uiId,
                  id: deleteTicketTarget.id,
                })
              : ""
          }
          ticketRef={
            deleteTicketTarget?.uiId &&
            deleteTicketTarget.uiId !== deleteTicketTarget.id
              ? deleteTicketTarget.uiId
              : undefined
          }
          onConfirm={confirmDeleteTicket}
          confirming={deleteTicketConfirming}
        />
      ) : null}
      {activeDetailTicketId ? (
        <AddTicketCommentDialog
          ticketId={activeDetailTicketId}
          open={addCommentOpen}
          onOpenChange={setAddCommentOpen}
        />
      ) : null}
    </div>
  );
}
